import { useState, useContext, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, ExternalLink, ChevronDown, X } from "lucide-react";
import { SiteContext } from "@/App";

export default function HeroSection() {
  const { settings } = useContext(SiteContext);
  const [eventOpen, setEventOpen] = useState(false);
  const [imageReady, setImageReady] = useState(false);

  const heroImage = settings?.hero_image_url;

  useEffect(() => {
    if (!heroImage) return;
    const img = new Image();
    img.src = heroImage;
    img.onload = () => setImageReady(true);
  }, [heroImage]);
  const eventEnabled = settings?.hero_event_enabled;

  useEffect(() => {
    if (settings?.hero_event_enabled) {
      const timer = setTimeout(() => setEventOpen(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [settings]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-top bg-no-repeat transition-opacity duration-700"
        style={{
          backgroundImage: imageReady ? `url(${heroImage})` : "none",
          opacity: imageReady ? 1 : 0,
        }}
      />
      {/* Dark overlays */}
      <div className="absolute inset-0 bg-black/65" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />

      {/* Content */}
      <div className={`relative z-10 text-center px-6 max-w-4xl mx-auto transition-transform duration-300 ${eventOpen ? "-translate-y-16 md:translate-y-0" : ""}`}>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-manrope text-verde text-xs tracking-[0.3em] uppercase mb-6"
        >
          Tattoo Artist &mdash; New Westminster, BC
        </motion.p>

        <motion.h1
          data-testid="hero-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="font-cinzel text-5xl sm:text-6xl lg:text-8xl font-black text-[#E5E5E5] mb-6 leading-tight tracking-widest"
        >
          LUD<br />BRADLEY
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="font-manrope text-[#A3A3A3] text-base sm:text-lg max-w-xl mx-auto mb-10 leading-relaxed"
        >
          Taking Black &amp; Grey Realism into the next century.
          Internationally recognized. Permanently unforgettable.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button
            data-testid="hero-cta-book"
            className="btn-verde"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          >
            Book a Consultation
          </button>
          <button
            data-testid="hero-cta-work"
            className="btn-ghost"
            onClick={() => document.getElementById("work")?.scrollIntoView({ behavior: "smooth" })}
          >
            View My Work
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
      >
        <span className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#737373]">Scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <ChevronDown size={16} className="text-[#737373]" />
        </motion.div>
      </motion.div>

      {/* Next Event Toggle Button */}
      {eventEnabled && (
        <motion.button
          data-testid="hero-event-toggle"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          onClick={() => setEventOpen(true)}
          className="absolute right-6 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-2 group"
        >
          <div className="w-px h-16 bg-verde/40 group-hover:bg-verde transition-colors" />
          <div className="bg-verde/10 border border-verde/30 rounded px-3 py-2 group-hover:bg-verde/20 transition-colors">
            <Calendar size={14} className="text-verde mb-1 mx-auto" />
            <span className="font-manrope text-[9px] tracking-widest uppercase text-verde whitespace-nowrap">Next Event</span>
          </div>
        </motion.button>
      )}

      {/* Mobile event button */}
      {eventEnabled && (
        <motion.button
          data-testid="hero-event-toggle-mobile"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          onClick={() => setEventOpen(true)}
          className="absolute bottom-20 md:hidden right-6 bg-verde/10 border border-verde/30 rounded-full px-4 py-2 flex items-center gap-2"
        >
          <Calendar size={12} className="text-verde" />
          <span className="font-manrope text-[9px] tracking-widest uppercase text-verde">Next Event</span>
        </motion.button>
      )}

      {/* Event Panel */}
      <AnimatePresence>
        {eventOpen && eventEnabled && (
          <motion.div
            data-testid="hero-event-panel"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="absolute bottom-0 left-0 right-0 z-20 bg-black/95 backdrop-blur-sm border-t border-verde/20 p-4 md:p-8"
          >
            <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <p className="font-manrope text-[10px] tracking-[0.3em] uppercase text-verde mb-2">
                  <Calendar size={10} className="inline mr-2" />
                  {settings?.hero_event_date || "Coming Soon"}
                </p>
                <h3 className="font-cinzel text-base md:text-2xl font-bold text-[#E5E5E5] mb-1 md:mb-2">
                  {settings?.hero_event_title || "Next Event"}
                </h3>
                <p className="font-manrope text-[#A3A3A3] text-xs md:text-sm max-w-lg leading-relaxed line-clamp-2 md:line-clamp-none">
                  {settings?.hero_event_description}
                </p>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {settings?.hero_event_link && (
                  <a
                    data-testid="hero-event-link"
                    href={settings.hero_event_link.startsWith("http") ? settings.hero_event_link : `https://${settings.hero_event_link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-verde flex items-center gap-2"
                  >
                    Learn More <ExternalLink size={12} />
                  </a>
                )}
                <button
                  data-testid="hero-event-close"
                  onClick={() => setEventOpen(false)}
                  className="text-[#525252] hover:text-[#E5E5E5] transition-colors p-2"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
